import { useEffect, useState } from 'react';
import { Truck, Package, CheckCircle, Shield, Hash, ChevronRight, FileText, Tag, Download, Printer, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../AuthContext';
import { useScrollHighlight } from '../hooks/useScrollHighlight';
import {
  adminGetShippingQueue, adminCreateShipment, adminListShipments,
  adminMarkShipped, adminGetShipment,
  downloadShipmentReleasePdf, downloadPackingSlipPdf, downloadCompletePacketPdf,
} from '../../services/api';

const STATUS_CFG = {
  preparing:   { label: 'Preparing',    cls: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
  ready:       { label: 'Ready',        cls: 'text-green-400 bg-green-500/10 border-green-500/20' },
  shipped:     { label: 'Shipped',      cls: 'text-teal-400 bg-teal-500/10 border-teal-500/20' },
  delivered:   { label: 'Delivered',    cls: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
};

const ORDER_STAGE = {
  in_production: { label: 'In Production', cls: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  serialized:    { label: 'Serialized',    cls: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
  ready_to_ship: { label: 'Ready to Ship', cls: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
};

function ShipModal({ order, onClose, onShipped }) {
  const { token } = useAdminAuth();
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('UPS');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [shipped, setShipped] = useState(false);
  const [error, setError] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState('');
  const [shipment, setShipment] = useState(null);
  const [view, setView] = useState('ship'); // ship | packing_slip | label

  const orderId = order._id || order.order_id;

  useEffect(() => {
    if (order.shipment_id) {
      adminGetShipment(token, order.shipment_id).then(setShipment).catch(console.error);
    }
  }, [order.shipment_id, token]);

  const handleShip = async () => {
    if (!tracking.trim()) { setError('Tracking number is required'); return; }
    setSaving(true);
    setError('');
    try {
      await adminMarkShipped(token, order.shipment_id, tracking, carrier, notes);
      onShipped();
      setShipped(true); // Show success state instead of closing
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const handleDownloadComplete = async () => {
    setPdfDownloading('complete');
    try { await downloadCompletePacketPdf(token, orderId, order.order_number, 'plain'); }
    catch (e) { alert(e.message); }
    setPdfDownloading('');
  };

  const handlePrintSection = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div data-testid="ship-modal" className="bg-[#111111] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="font-bebas text-xl text-white tracking-wider">{order.order_number}</h2>
            <p className="font-inter text-xs text-zinc-500">{order.business_name || order.customer_info?.name} · {order.serial_count || 0} serials</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {!shipped && ['ship', 'packing_slip', 'label'].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`font-inter text-xs px-3 py-1.5 border transition-colors uppercase tracking-wider ${view === v ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'}`}>
                {v === 'ship' ? 'Ship' : v === 'packing_slip' ? 'Packing Slip' : 'Label'}
              </button>
            ))}
            <button
              data-testid="download-complete-packet"
              onClick={handleDownloadComplete}
              disabled={!!pdfDownloading}
              className="flex items-center gap-1 font-inter text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 transition-colors disabled:opacity-50"
              title="Download complete packet (all documents in one PDF)"
            >
              <Download size={11} />{pdfDownloading === 'complete' ? '...' : 'All Docs'}
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-white px-2">✕</button>
          </div>
        </div>

        <div className="p-5">

          {/* SUCCESS STATE after confirming shipment */}
          {shipped && (
            <div data-testid="ship-success-state" className="text-center py-8">
              <CheckCircle size={48} className="text-teal-400 mx-auto mb-4" />
              <p className="font-bebas text-2xl text-white tracking-wider mb-1">SHIPMENT CONFIRMED</p>
              <p className="font-inter text-sm text-zinc-400 mb-6">
                {order.order_number} has been marked as shipped. Download all paperwork below.
              </p>
              <div className="grid grid-cols-1 gap-3 max-w-sm mx-auto">
                <button
                  data-testid="download-all-docs-success"
                  onClick={handleDownloadComplete}
                  disabled={!!pdfDownloading}
                  className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-base tracking-widest px-5 py-4 transition-colors w-full"
                >
                  <Download size={16} />
                  {pdfDownloading === 'complete' ? 'GENERATING...' : 'DOWNLOAD COMPLETE PACKET PDF'}
                </button>
                <p className="font-inter text-xs text-zinc-600">Includes: shipping label + packing slip + serial records + NFA compliance</p>
              </div>
            </div>
          )}

          {/* Mark Shipped */}
          {!shipped && view === 'ship' && (
            <div>
              {order.has_nfa_items && (
                <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/30 p-3 mb-4">
                  <Shield size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />
                  <p className="font-inter text-xs text-orange-300">NFA Items: UPS shipping required. Verify FFL transfer approval before shipping.</p>
                </div>
              )}
              {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">{error}</div>}
              <div className="space-y-3">
                <div>
                  <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Carrier</label>
                  <select value={carrier} onChange={e => setCarrier(e.target.value)}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2.5 outline-none transition-colors">
                    {['UPS', 'FedEx', 'USPS', 'Manual'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Tracking Number *</label>
                  <input
                    data-testid="tracking-number-input"
                    value={tracking} onChange={e => setTracking(e.target.value)}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2.5 outline-none transition-colors font-mono placeholder-zinc-600"
                    placeholder={carrier === 'UPS' ? '1Z...' : 'Enter tracking number'}
                  />
                </div>
                <div>
                  <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors resize-none placeholder-zinc-600"
                    placeholder="Optional shipping notes..." />
                </div>
                <button
                  data-testid="confirm-ship-button"
                  onClick={handleShip} disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-lg tracking-widest py-3 transition-colors">
                  <Truck size={18} />{saving ? 'MARKING SHIPPED...' : 'CONFIRM SHIPMENT'}
                </button>
              </div>
            </div>
          )}

          {/* Packing Slip */}
          {!shipped && view === 'packing_slip' && (
            <div data-testid="packing-slip-view">
              {/* PDF download + print buttons */}
              <div className="flex gap-2 mb-4">
                <button
                  data-testid="download-packing-slip"
                  onClick={async () => { setPdfDownloading('slip'); try { await downloadPackingSlipPdf(token, orderId, order.order_number, 'plain'); } catch(e) { alert(e.message); } setPdfDownloading(''); }}
                  disabled={!!pdfDownloading}
                  className="flex items-center gap-1.5 font-inter text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 transition-colors disabled:opacity-50"
                >
                  <Download size={13} />{pdfDownloading === 'slip' ? 'Generating...' : 'Download Packing Slip PDF'}
                </button>
                <button
                  data-testid="download-all-docs"
                  onClick={handleDownloadComplete}
                  disabled={!!pdfDownloading}
                  className="flex items-center gap-1.5 font-inter text-xs bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-2 transition-colors disabled:opacity-50"
                >
                  <Download size={13} />{pdfDownloading === 'complete' ? 'Generating...' : 'Download All Documents'}
                </button>
              </div>
              {shipment?.packing_slip ? (
              <div className="bg-[#0f0f0f] border border-white/10 p-5 font-inter">
                <div className="flex items-start justify-between mb-5 pb-4 border-b border-white/10">
                  <div>
                    <div className="font-bebas text-2xl text-white tracking-wider">WITT MACHINE & TOOL CO.</div>
                    <div className="font-inter text-xs text-zinc-500">Texas, USA</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bebas text-lg text-orange-500 tracking-wider">PACKING SLIP</div>
                    <div className="font-inter text-xs text-zinc-500">#{shipment.packing_slip.slip_number}</div>
                    <div className="font-inter text-[10px] text-zinc-600">{new Date(shipment.packing_slip.generated_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div>
                    <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Ship To</p>
                    {Object.entries(shipment.packing_slip.to_address || {}).filter(([,v]) => v).map(([k, v]) => (
                      <p key={k} className="font-inter text-xs text-white">{v}</p>
                    ))}
                  </div>
                  <div>
                    <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Order</p>
                    <p className="font-inter text-xs text-white">{shipment.packing_slip.order_number}</p>
                    <p className="font-inter text-xs text-zinc-400">{shipment.packing_slip.customer_name}</p>
                  </div>
                </div>
                <table className="w-full text-xs mb-4">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 font-inter text-zinc-500 uppercase tracking-wider">Product</th>
                      <th className="text-center py-2 font-inter text-zinc-500 uppercase tracking-wider">Qty</th>
                      <th className="text-left py-2 font-inter text-zinc-500 uppercase tracking-wider">Serial #(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipment.packing_slip.items?.map((item, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="py-2">
                          <span className="text-white">{item.product_name}</span>
                          {item.nfa_item && <span className="ml-2 text-orange-400">[NFA]</span>}
                        </td>
                        <td className="py-2 text-center text-white">{item.quantity}</td>
                        <td className="py-2">
                          {item.serial_numbers?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {item.serial_numbers.map(sn => <span key={sn} className="font-mono text-orange-300 text-[10px]">{sn}</span>)}
                            </div>
                          ) : <span className="text-zinc-600">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              ) : (
                <div className="text-center py-8 text-zinc-500 font-inter text-sm">
                  <Package size={32} className="mx-auto mb-3 text-zinc-700" />
                  Packing slip will be available after creating the shipment.
                  <br/><button onClick={handleDownloadComplete} disabled={!!pdfDownloading} className="mt-3 font-inter text-xs text-orange-400 hover:text-orange-300 underline">Download full packet instead</button>
                </div>
              )}
            </div>
          )}

          {/* Shipping Label */}
          {!shipped && view === 'label' && (
            <div data-testid="shipping-label-view">
              {/* PDF download button */}
              <div className="flex gap-2 mb-4">
                <button
                  data-testid="download-complete-packet-from-label"
                  onClick={handleDownloadComplete}
                  disabled={!!pdfDownloading}
                  className="flex items-center gap-1.5 font-inter text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 transition-colors disabled:opacity-50"
                >
                  <Download size={13} />{pdfDownloading === 'complete' ? 'Generating...' : 'Download Complete Packet (includes label)'}
                </button>
              </div>
              {shipment?.shipping_label ? (
              <div className="bg-[#0f0f0f] border border-white/10 p-5">
                {/* Enhanced UPS-style label */}
                <div className="border-2 border-white/30 p-5">
                  {/* Carrier + Service header */}
                  <div className="flex items-start justify-between mb-5 pb-4 border-b border-white/20">
                    <div>
                      <div className="font-bebas text-4xl text-white tracking-wide">{shipment.shipping_label.carrier || 'UPS'}</div>
                      <div className="font-inter text-xs text-zinc-400">{shipment.shipping_label.service || 'UPS Ground'}</div>
                    </div>
                    {order.has_nfa_items && (
                      <div className="border-2 border-orange-500 px-3 py-2 text-center">
                        <div className="font-bebas text-xs text-orange-400 tracking-widest">NFA ITEMS</div>
                        <div className="font-inter text-[9px] text-orange-400">FFL REQUIRED</div>
                      </div>
                    )}
                  </div>
                  {/* From/To */}
                  <div className="grid grid-cols-3 gap-4 mb-5">
                    <div>
                      <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-widest mb-1">FROM</p>
                      <p className="font-inter text-xs text-white font-semibold">{shipment.shipping_label.from_address?.name}</p>
                      <p className="font-inter text-[10px] text-zinc-500">{shipment.shipping_label.from_address?.city}, {shipment.shipping_label.from_address?.state}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="font-inter text-[9px] text-zinc-600 uppercase tracking-widest mb-1">SHIP TO</p>
                      <p className="font-inter text-base text-white font-bold leading-tight">
                        {shipment.shipping_label.to_address?.name}
                      </p>
                      <p className="font-inter text-sm text-white">{shipment.shipping_label.to_address?.street}</p>
                      <p className="font-inter text-sm text-white">
                        {shipment.shipping_label.to_address?.city}{shipment.shipping_label.to_address?.city && shipment.shipping_label.to_address?.state ? ', ' : ''}
                        {shipment.shipping_label.to_address?.state} {shipment.shipping_label.to_address?.zip}
                      </p>
                    </div>
                  </div>
                  {/* Barcode area */}
                  <div className="bg-black border border-zinc-800 p-4 mb-4 text-center">
                    <div className="flex justify-center items-end gap-px mb-3">
                      {/* CSS Barcode simulation */}
                      {(shipment.shipping_label.tracking_number || 'MOCK').split('').map((char, i) => {
                        const h = (char.charCodeAt(0) * 7 + i * 13) % 48 + 20;
                        const w = (char.charCodeAt(0) % 3) + 1;
                        return <div key={i} style={{width: `${w * 1.5}px`, height: `${h}px`, background:'white'}} />;
                      })}
                    </div>
                    <p className="font-mono text-xl font-bold text-white tracking-widest">
                      {shipment.shipping_label.tracking_number || '(Tracking assigned on ship)'}
                    </p>
                    <p className="font-inter text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">Tracking Number</p>
                  </div>
                  {/* References */}
                  <div className="grid grid-cols-3 gap-3 text-xs border-t border-white/10 pt-3">
                    <div><span className="text-zinc-600 uppercase text-[9px] tracking-wider block">Ref 1</span><span className="text-white font-mono">{shipment.shipping_label.reference_1 || '—'}</span></div>
                    <div><span className="text-zinc-600 uppercase text-[9px] tracking-wider block">Ref 2</span><span className="text-white font-mono">{shipment.shipping_label.reference_2 || '—'}</span></div>
                    <div><span className="text-zinc-600 uppercase text-[9px] tracking-wider block">Weight</span><span className="text-white">2.0 LBS</span></div>
                  </div>
                  {shipment.shipping_label.special_instructions && (
                    <div className="mt-3 bg-orange-500/10 border border-orange-500/30 p-2 text-center">
                      <p className="font-inter text-[10px] text-orange-400 font-bold">{shipment.shipping_label.special_instructions}</p>
                    </div>
                  )}
                  <p className="text-center text-[9px] text-zinc-700 mt-3">MOCK LABEL — FOR INTERNAL REFERENCE ONLY</p>
                </div>
              </div>
              ) : (
                <div className="text-center py-8 text-zinc-500 font-inter text-sm">
                  <Tag size={32} className="mx-auto mb-3 text-zinc-700" />
                  Label will be available after the shipment is created.
                  <br/><button onClick={handleDownloadComplete} disabled={!!pdfDownloading} className="mt-3 font-inter text-xs text-orange-400 hover:text-orange-300 underline">Download full packet (includes label)</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminShipping() {
  const { token } = useAdminAuth();
  const highlightId = useScrollHighlight();
  const [tab, setTab] = useState('queue');
  const [queue, setQueue] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState('');

  const loadQueue = () => {
    adminGetShippingQueue(token).then(q => {
      setQueue(q.filter(o => ['serialized', 'ready_to_ship', 'in_production', 'ready_for_production'].includes(o.status)));
    }).catch(console.error);
  };

  const loadShipments = () => {
    adminListShipments(token).then(setShipments).catch(console.error);
  };

  useEffect(() => {
    Promise.all([
      adminGetShippingQueue(token).then(q => setQueue(q.filter(o => ['in_production', 'serialized', 'ready_to_ship', 'ready_for_production'].includes(o.status)))),
      adminListShipments(token).then(setShipments),
    ]).finally(() => setLoading(false));
  }, [token]);

  const handleCreateShipment = async (order) => {
    setCreatingFor(order._id);
    try {
      await adminCreateShipment(token, order._id, 'UPS', '');
      loadQueue();
      loadShipments();
    } catch (err) { alert(err.message); }
    setCreatingFor(null);
  };

  const readyToShip = queue.filter(o => o.status === 'ready_to_ship');
  const inProgress = queue.filter(o => ['in_production', 'serialized', 'ready_for_production'].includes(o.status));
  const shippedShipments = shipments.filter(s => s.status === 'shipped');
  const completedShipments = shipments.filter(s => s.status === 'completed');
  const activeShipments = shipments.filter(s => !['shipped','completed'].includes(s.status));

  const handleMarkComplete = async (shipment) => {
    if (!window.confirm(`Mark shipment ${shipment.shipment_id} as Complete and archive it?`)) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/shipments/${shipment.shipment_id}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json(); alert(e.detail || 'Failed'); return; }
      loadShipments();
    } catch (e) { alert(e.message); }
  };

  return (
    <div data-testid="admin-shipping-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">SHIPPING DASHBOARD</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Manage shipments from production to delivery</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { val: readyToShip.length, label: 'Ready to Ship', color: 'text-orange-400' },
          { val: activeShipments.length, label: 'Active Shipments', color: 'text-yellow-400' },
          { val: shippedShipments.length, label: 'Shipped', color: 'text-teal-400' },
          { val: completedShipments.length, label: 'Completed', color: 'text-green-400' },
        ].map(s => (
          <div key={s.label} className="bg-[#1a1a1a] border border-white/10 p-4 text-center">
            <div className={`font-bebas text-2xl tracking-wider ${s.color}`}>{s.val}</div>
            <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>


      {/* Tabs — single nav */}
      <div className="flex gap-1 mb-5 border-b border-white/10">
        {[
          { key: 'queue', label: `Shipping Queue (${readyToShip.length + queue.filter(o => o.status === 'serialized').length})` },
          { key: 'active', label: `Active Shipments (${activeShipments.length})` },
          { key: 'shipped', label: `Shipped (${shippedShipments.length})` },
          { key: 'completed', label: `Completed (${completedShipments.length})` },
        ].map(t => (
          <button key={t.key} data-testid={`shipping-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`font-inter text-sm px-4 py-2.5 transition-colors border-b-2 -mb-px ${tab === t.key ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Shipping Queue tab — shows BOTH serialized (needs shipment) AND ready_to_ship (needs tracking) */}
      {tab === 'queue' && (
        <div data-testid="shipping-queue-tab">
          {/* Stage A: Serialized → Create Shipment */}
          {queue.filter(o => o.status === 'serialized').length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-6 h-6 bg-purple-500/20 flex items-center justify-center"><span className="font-bebas text-sm text-purple-400">1</span></div>
                <span className="font-bebas text-lg text-purple-400 tracking-wider">SERIALIZED — CREATE SHIPMENT</span>
                <span className="font-inter text-xs text-zinc-500">All items have serial numbers assigned. Click to create shipment.</span>
              </div>
              <div className="space-y-2">
                {queue.filter(o => o.status === 'serialized').map(o => (
                  <div
                    key={o._id}
                    id={`hl-${o._id}`}
                    data-testid={`serialized-order-${o._id}`}
                    className={`bg-[#1a1a1a] border p-4 flex items-center justify-between transition-all duration-500 ${
                      highlightId === o._id ? 'border-orange-500 shadow-[0_0_0_3px_rgba(249,115,22,0.25)]' : 'border-purple-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/orders/${o._id}`}
                            className="font-inter text-sm font-semibold text-orange-400 hover:text-white transition-colors"
                            title="View order detail"
                          >
                            {o.order_number}
                          </Link>
                          {o.has_nfa_items && <span className="font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5">NFA</span>}
                          <span className="font-inter text-[10px] text-purple-400 border border-purple-500/20 bg-purple-500/10 px-2 py-0.5 uppercase tracking-wider">Serialized</span>
                          <Link to={`/admin/orders/${o._id}`} className="flex items-center gap-1 font-inter text-[10px] text-zinc-500 hover:text-orange-400 transition-colors" title="View order detail">
                            <ExternalLink size={9} />Order
                          </Link>
                        </div>
                        <span className="font-inter text-xs text-zinc-500">{o.business_name || o.customer_info?.name} · {o.serial_count || 0} serials · ${o.subtotal?.toFixed(2)}</span>
                      </div>
                    </div>
                    <button
                      data-testid={`create-shipment-serialized-${o._id}`}
                      onClick={() => handleCreateShipment(o)}
                      disabled={creatingFor === o._id}
                      className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-bebas text-sm tracking-widest px-4 py-2.5 transition-colors"
                    >
                      <Package size={14} />{creatingFor === o._id ? 'CREATING...' : 'CREATE SHIPMENT →'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stage B: Ready to Ship → Enter Tracking */}
          {readyToShip.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-6 h-6 bg-orange-500/20 flex items-center justify-center"><span className="font-bebas text-sm text-orange-400">2</span></div>
                <span className="font-bebas text-lg text-orange-400 tracking-wider">READY TO SHIP — ENTER TRACKING</span>
                <span className="font-inter text-xs text-zinc-500">Shipment created. Enter carrier tracking number to complete.</span>
              </div>
              <div className="space-y-2">
                {readyToShip.map(o => (
                  <div
                    key={o._id}
                    id={`hl-${o._id}`}
                    data-testid={`ship-order-${o._id}`}
                    className={`bg-[#1a1a1a] border p-4 flex items-center justify-between transition-all duration-500 ${
                      highlightId === o._id ? 'border-orange-500 shadow-[0_0_0_3px_rgba(249,115,22,0.25)]' : 'border-orange-500/30'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <Link
                          to={`/admin/orders/${o._id}`}
                          className="font-inter text-sm font-semibold text-orange-400 hover:text-white transition-colors"
                          title="View order detail"
                        >
                          {o.order_number}
                        </Link>
                        {o.has_nfa_items && <span className="flex items-center gap-1 font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5"><Shield size={9} />NFA</span>}
                        <span className="font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-2 py-0.5 uppercase tracking-wider">Ready to Ship</span>
                      </div>
                      <span className="font-inter text-xs text-zinc-500">{o.business_name || o.customer_info?.name} · {o.serial_count || 0} serials · ${o.subtotal?.toFixed(2)}</span>
                    </div>
                    <button
                      data-testid={`mark-shipped-${o._id}`}
                      onClick={() => setSelectedOrder(o)}
                      className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-sm tracking-widest px-4 py-2.5 transition-colors"
                    >
                      <Truck size={14} />ENTER TRACKING &amp; SHIP
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {queue.filter(o => ['serialized','ready_to_ship'].includes(o.status)).length === 0 && (
            <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
              <Truck size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">SHIPPING QUEUE EMPTY</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">Orders appear here after being serialized</p>
            </div>
          )}
        </div>
      )}

      {/* Active Shipments tab */}
      {tab === 'active' && (
        <div data-testid="active-shipments-tab">
          {activeShipments.length === 0 ? (
            <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
              <Package size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO ACTIVE SHIPMENTS</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeShipments.map(s => {
                const cfg = STATUS_CFG[s.status] || STATUS_CFG.preparing;
                return (
                  <div key={s.shipment_id} data-testid={`shipment-${s.shipment_id}`} className="bg-[#1a1a1a] border border-white/10 p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-inter text-sm font-semibold text-white">{s.shipment_id}</span>
                          <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>
                          {s.has_nfa_items && <span className="flex items-center gap-1 font-inter text-[10px] text-orange-400"><Shield size={9} />NFA</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-inter text-xs text-zinc-500">{s.order_number} · {s.business_name}</span>
                          <span className="font-inter text-xs text-zinc-600">·</span>
                          <span className="font-inter text-xs text-zinc-500">{s.serial_numbers?.length || 0} serials · {s.carrier}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const o = queue.find(q => q.shipment_id === s.shipment_id) || { _id: s.order_id, order_number: s.order_number, business_name: s.business_name, shipment_id: s.shipment_id, has_nfa_items: s.has_nfa_items, serial_count: s.serial_numbers?.length };
                          setSelectedOrder(o);
                        }}
                        className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-inter text-sm font-medium px-3 py-2 transition-colors"
                      >
                        <Truck size={13} />Ship It
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Shipped history tab */}
      {tab === 'shipped' && (
        <div data-testid="shipped-tab">
          {shippedShipments.length === 0 ? (
            <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
              <CheckCircle size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO SHIPMENTS YET</p>
            </div>
          ) : (
            <div className="bg-[#1a1a1a] border border-white/10 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    {['Shipment', 'Order', 'Business', 'Serials', 'Carrier', 'Tracking', 'Shipped', 'PDFs'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-inter text-xs text-zinc-500 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {shippedShipments.map(s => (
                    <tr key={s.shipment_id} className="hover:bg-white/3">
                      <td className="px-4 py-3 font-inter text-sm font-semibold text-white">{s.shipment_id}</td>
                      <td className="px-4 py-3">
                        {s.order_id ? (
                          <Link
                            to={`/admin/orders/${s.order_id}`}
                            className="font-inter text-xs text-orange-400 hover:text-white transition-colors"
                            title="View order detail"
                          >
                            {s.order_number}
                          </Link>
                        ) : (
                          <span className="font-inter text-xs text-zinc-400">{s.order_number || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-inter text-xs text-zinc-400">{s.business_name || '—'}</td>
                      <td className="px-4 py-3 text-center font-inter text-sm text-zinc-400">{s.serial_numbers?.length || 0}</td>
                      <td className="px-4 py-3 font-inter text-xs text-zinc-400">{s.carrier}</td>
                      <td className="px-4 py-3 font-inter text-xs text-teal-400 font-mono">{s.tracking_number || '—'}</td>
                      <td className="px-4 py-3 font-inter text-xs text-zinc-500">
                        {s.shipped_at ? new Date(s.shipped_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button
                            data-testid={`release-pdf-${s.shipment_id}`}
                            onClick={async () => {
                              setPdfDownloading(s.shipment_id + '_r');
                              try { await downloadShipmentReleasePdf(token, s.shipment_id, 'plain'); }
                              catch(e) { alert(e.message); }
                              setPdfDownloading('');
                            }}
                            disabled={!!pdfDownloading}
                            className="flex items-center gap-1 font-inter text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 transition-colors disabled:opacity-40"
                            title="Download Shipment Release PDF"
                          >
                            <Download size={9} />Release
                          </button>
                          {s.order_id && (
                            <button
                              data-testid={`complete-packet-pdf-${s.shipment_id}`}
                              onClick={async () => {
                                setPdfDownloading(s.shipment_id + '_p');
                                try { await downloadCompletePacketPdf(token, s.order_id, s.order_number, 'plain'); }
                                catch(e) { alert(e.message); }
                                setPdfDownloading('');
                              }}
                              disabled={!!pdfDownloading}
                              className="flex items-center gap-1 font-inter text-[10px] bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 transition-colors disabled:opacity-40"
                              title="Download Complete Packet (all documents)"
                            >
                              <Download size={9} />All Docs
                            </button>
                          )}
                          {/* MARK COMPLETE — archives this shipment */}
                          <button
                            data-testid={`mark-complete-${s.shipment_id}`}
                            onClick={() => handleMarkComplete(s)}
                            className="flex items-center gap-1 font-inter text-[10px] bg-green-600 hover:bg-green-500 text-white px-2 py-1 transition-colors font-semibold"
                            title="Mark complete — archives this shipment"
                          >
                            <CheckCircle size={9} />Complete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* COMPLETED TAB — archived shipments */}
      {tab === 'completed' && (
        <div data-testid="completed-tab">
          {completedShipments.length === 0 ? (
            <div className="text-center py-14 bg-[#1a1a1a] border border-white/10">
              <CheckCircle size={32} className="text-zinc-700 mx-auto mb-3" />
              <p className="font-bebas text-xl text-zinc-600 tracking-wider">NO COMPLETED SHIPMENTS</p>
              <p className="font-inter text-sm text-zinc-600 mt-1">Mark shipped orders as "Complete" to archive them here</p>
            </div>
          ) : (
            <div className="bg-[#1a1a1a] border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#141414] border-b border-white/10">
                  <tr>
                    {['Shipment ID','Order #','Customer','Serials','Carrier','Tracking','Shipped','Completed','Docs'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-inter text-xs text-zinc-500 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {completedShipments.map(s => (
                    <tr key={s.shipment_id} className="hover:bg-white/2 opacity-80">
                      <td className="px-4 py-3 font-inter text-sm font-semibold text-zinc-400">{s.shipment_id}</td>
                      <td className="px-4 py-3">
                        {s.order_id ? (
                          <Link to={`/admin/orders/${s.order_id}`}
                            className="font-inter text-xs text-orange-400 hover:text-white transition-colors">
                            {s.order_number}
                          </Link>
                        ) : <span className="font-inter text-xs text-zinc-500">{s.order_number || '—'}</span>}
                      </td>
                      <td className="px-4 py-3 font-inter text-xs text-zinc-500">{s.business_name || '—'}</td>
                      <td className="px-4 py-3 text-center font-inter text-sm text-zinc-500">{s.serial_numbers?.length || 0}</td>
                      <td className="px-4 py-3 font-inter text-xs text-zinc-500">{s.carrier}</td>
                      <td className="px-4 py-3 font-inter text-xs text-zinc-500 font-mono">{s.tracking_number || '—'}</td>
                      <td className="px-4 py-3 font-inter text-xs text-zinc-600">
                        {s.shipped_at ? new Date(s.shipped_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 font-inter text-xs text-green-500">
                        {s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {s.order_id && (
                          <button
                            onClick={async () => {
                              setPdfDownloading(s.shipment_id + '_cp');
                              try { await downloadCompletePacketPdf(token, s.order_id, s.order_number, 'plain'); }
                              catch(e) { alert(e.message); }
                              setPdfDownloading('');
                            }}
                            disabled={!!pdfDownloading}
                            className="flex items-center gap-1 font-inter text-[10px] bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 transition-colors disabled:opacity-40"
                          >
                            <Download size={9} />Docs
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Ship Modal */}
      {selectedOrder && (
        <ShipModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onShipped={() => { loadQueue(); loadShipments(); }}
        />
      )}
    </div>
  );
}
